import { organizationScanAlertsQuerySchema, alertsQuerySchema, alertOccurrencesQuerySchema, alertOccurrencesResponseSchema, alertRefResponseSchema, alertFilterOptionsQuerySchema, alertFilterOptionsResponseSchema, userScanAlertsQuerySchema, userAlertsFilterOptionsQuerySchema, groupedAlertsResponseSchema, groupedAlertsTableResponseSchema, muteAlertRequestSchema, mutedAlertStateSchema, excludeAlertRequestSchema, excludedAlertStateSchema, setAlertStatusRequestSchema, setAlertCommentRequestSchema, alertFeedbackStateSchema, overrideSeverityRequestSchema, severityOverrideStateSchema, bulkAlertActionSchema, bulkAlertApplyRequestSchema, bulkAlertApplyResponseSchema, bulkAlertEligibilityRequestSchema, bulkAlertEligibilityResponseSchema, } from '../contracts/alerts';
import { serializeTableQueryParams } from '../contracts/table';
import { parseSchema } from '../errors';
/**
 * List grouped alerts for an organization with pagination, sorting, and search.
 */
export async function listOrganizationGroupedAlerts(client, organizationId, params = {}, options) {
    const validatedParams = parseSchema(organizationScanAlertsQuerySchema, params);
    const response = await client.get(`/organizations/${organizationId}/alerts`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(groupedAlertsResponseSchema, response);
}
/**
 * List individual occurrences for a specific grouped alert.
 */
export async function listAlertOccurrences(client, organizationId, alertKey, params = {}, options) {
    const validatedParams = parseSchema(alertOccurrencesQuerySchema, params);
    const response = await client.get(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/occurrences`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(alertOccurrencesResponseSchema, response);
}
/**
 * KIR-2788: resolve a notification token to the exact alert(s) it pointed at.
 * The token is one opaque 64-hex value — a finding_key (one alert) or an
 * alert-set id (composite / secret) — and this one call resolves either. Simpler
 * than occurrences: no query params; the token fully addresses the alert(s). A
 * 404 (unknown / expired / wrong-scope token) surfaces as an ApiError for the
 * caller to degrade to the filtered list.
 */
export async function resolveAlertRef(client, organizationId, token, options) {
    const response = await client.get(`/organizations/${organizationId}/alerts/ref/${encodeURIComponent(token)}`, { signal: options?.signal });
    return parseSchema(alertRefResponseSchema, response);
}
/**
 * KIR-2788: personal (account) variant of {@link resolveAlertRef} — resolves a
 * personal-account user's notification token (`/dashboard/alerts/{token}`) to
 * the exact alert(s), scoped to the account owner. Same response shape as the
 * org resolve.
 */
export async function resolveAccountAlertRef(client, token, options) {
    const response = await client.get(`/account/alerts/ref/${encodeURIComponent(token)}`, { signal: options?.signal });
    return parseSchema(alertRefResponseSchema, response);
}
/**
 * List grouped alerts for the authenticated user with pagination and sorting.
 *
 * @deprecated The server now always returns the table-engine shape for
 * GET /account/alerts. Use {@link listUserGroupedDataTableAlerts} instead,
 * which parses the response with the correct {@link groupedAlertsTableResponseSchema}.
 */
export async function listUserGroupedAlerts(client, params = {}) {
    const validatedParams = parseSchema(alertsQuerySchema, params);
    const response = await client.get('/account/alerts', {
        params: validatedParams,
    });
    return parseSchema(groupedAlertsResponseSchema, response);
}
/**
 * List individual occurrences for a specific user grouped alert.
 */
export async function listUserAlertOccurrences(client, alertKey, params = {}) {
    const validatedParams = parseSchema(alertOccurrencesQuerySchema, params);
    const response = await client.get(`/account/alerts/${encodeURIComponent(alertKey)}/occurrences`, { params: validatedParams, paramsSerializer: serializeTableQueryParams });
    return parseSchema(alertOccurrencesResponseSchema, response);
}
/**
 * Get filter options for organization alerts.
 * Returns distinct values for the specified field (severity or users).
 */
export async function getOrganizationAlertFilterOptions(client, organizationId, params) {
    const validatedParams = parseSchema(alertFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${organizationId}/alerts/filter-options`, { params: validatedParams });
    return parseSchema(alertFilterOptionsResponseSchema, response);
}
/**
 * List grouped alerts for the authenticated user with pagination, sorting,
 * filters, and search. Always returns the table engine response shape.
 */
export async function listUserGroupedDataTableAlerts(client, params = {}, options) {
    const validatedParams = parseSchema(userScanAlertsQuerySchema, params);
    const response = await client.get('/account/alerts', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(groupedAlertsTableResponseSchema, response);
}
/**
 * Get filter options for user alerts.
 * Returns distinct values for the specified field (type or filePath).
 */
export async function getUserAlertFilterOptions(client, params) {
    const validatedParams = parseSchema(userAlertsFilterOptionsQuerySchema, params);
    const response = await client.get('/account/alerts/filter-options', {
        params: validatedParams,
    });
    return parseSchema(alertFilterOptionsResponseSchema, response);
}
/**
 * Mute an organization-scoped grouped alert.
 *
 * The alert key is path-encoded so callers can pass arbitrary keys without
 * pre-encoding. The body is validated against {@link muteAlertRequestSchema}
 * before sending. The response is the canonical {@link MutedAlertState}
 * fragment for the alert after the mutation.
 */
export async function muteOrganizationAlert(client, organizationId, alertKey, body) {
    const validatedBody = parseSchema(muteAlertRequestSchema, body);
    const response = await client.post(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/mute`, validatedBody);
    return parseSchema(mutedAlertStateSchema, response);
}
/**
 * Unmute an organization-scoped grouped alert.
 *
 * Returns the canonical {@link MutedAlertState} fragment after the mutation
 * (with `muted: false` and all timestamps cleared).
 */
export async function unmuteOrganizationAlert(client, organizationId, alertKey) {
    const response = await client.delete(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/mute`);
    return parseSchema(mutedAlertStateSchema, response);
}
/**
 * Set the feedback status (verdict) on an organization-scoped grouped alert
 * (KIR-4440). Manager/owner only — the backend 403s other roles. Returns the
 * canonical {@link AlertFeedbackState} fragment after the mutation.
 *
 * Routes 404 when the `alert_feedback` feature flag is disabled.
 */
export async function setOrganizationAlertStatus(client, organizationId, alertKey, body) {
    const validatedBody = parseSchema(setAlertStatusRequestSchema, body);
    const response = await client.post(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/status`, validatedBody);
    return parseSchema(alertFeedbackStateSchema, response);
}
/**
 * Set the free-text comment on an organization-scoped grouped alert
 * (KIR-4440). Any org member (excluding support) may edit. An empty string
 * clears the comment. Returns the canonical {@link AlertFeedbackState}
 * fragment after the mutation.
 *
 * Routes 404 when the `alert_feedback` feature flag is disabled.
 */
export async function setOrganizationAlertComment(client, organizationId, alertKey, body) {
    const validatedBody = parseSchema(setAlertCommentRequestSchema, body);
    const response = await client.post(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/comment`, validatedBody);
    return parseSchema(alertFeedbackStateSchema, response);
}
/**
 * Mute an account-scoped (self) grouped alert.
 *
 * The alert key is path-encoded; the body is validated against
 * {@link muteAlertRequestSchema}. Returns the canonical
 * {@link MutedAlertState} fragment for the alert after the mutation.
 */
export async function muteAccountAlert(client, alertKey, body) {
    const validatedBody = parseSchema(muteAlertRequestSchema, body);
    const response = await client.post(`/account/alerts/${encodeURIComponent(alertKey)}/mute`, validatedBody);
    return parseSchema(mutedAlertStateSchema, response);
}
/**
 * Unmute an account-scoped (self) grouped alert.
 */
export async function unmuteAccountAlert(client, alertKey) {
    const response = await client.delete(`/account/alerts/${encodeURIComponent(alertKey)}/mute`);
    return parseSchema(mutedAlertStateSchema, response);
}
/**
 * Override the severity of an organization-scoped grouped alert (KIR-2364).
 *
 * The alert key is path-encoded; the body is validated against
 * {@link overrideSeverityRequestSchema}. Returns the canonical
 * {@link SeverityOverrideState} fragment for the alert after the mutation.
 *
 * Routes 404 when the `alert_severity_override` feature flag is disabled, which
 * is surfaced as a `NotFoundError` from the shared client interceptor.
 */
export async function overrideOrganizationAlertSeverity(client, organizationId, alertKey, body) {
    const validatedBody = parseSchema(overrideSeverityRequestSchema, body);
    const response = await client.post(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/severity`, validatedBody);
    return parseSchema(severityOverrideStateSchema, response);
}
/**
 * Reset an organization-scoped grouped alert's severity to Kirin's default
 * (KIR-2364). Returns the canonical {@link SeverityOverrideState} fragment
 * after the mutation (with `overridden: false`).
 */
export async function resetOrganizationAlertSeverity(client, organizationId, alertKey) {
    const response = await client.delete(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/severity`);
    return parseSchema(severityOverrideStateSchema, response);
}
/**
 * Override the severity of an account-scoped (self) grouped alert (KIR-2364).
 *
 * The alert key is path-encoded; the body is validated against
 * {@link overrideSeverityRequestSchema}. Returns the canonical
 * {@link SeverityOverrideState} fragment for the alert after the mutation.
 */
export async function overrideAccountAlertSeverity(client, alertKey, body) {
    const validatedBody = parseSchema(overrideSeverityRequestSchema, body);
    const response = await client.post(`/account/alerts/${encodeURIComponent(alertKey)}/severity`, validatedBody);
    return parseSchema(severityOverrideStateSchema, response);
}
/**
 * Reset an account-scoped (self) grouped alert's severity to Kirin's default
 * (KIR-2364).
 */
export async function resetAccountAlertSeverity(client, alertKey) {
    const response = await client.delete(`/account/alerts/${encodeURIComponent(alertKey)}/severity`);
    return parseSchema(severityOverrideStateSchema, response);
}
/**
 * Exclude an organization-scoped grouped alert (KIR-2363).
 *
 * Mirror of {@link muteOrganizationAlert}: path-encoded key, validated
 * body, canonical {@link ExcludedAlertState} response.
 */
export async function excludeOrganizationAlert(client, organizationId, alertKey, body) {
    const validatedBody = parseSchema(excludeAlertRequestSchema, body);
    const response = await client.post(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/exclude`, validatedBody);
    return parseSchema(excludedAlertStateSchema, response);
}
/**
 * Remove the exclusion from an organization-scoped grouped alert.
 *
 * Returns the canonical {@link ExcludedAlertState} fragment after the
 * mutation (with `excluded: false` and all timestamps cleared).
 */
export async function removeOrganizationAlertExclusion(client, organizationId, alertKey) {
    const response = await client.delete(`/organizations/${organizationId}/alerts/${encodeURIComponent(alertKey)}/exclude`);
    return parseSchema(excludedAlertStateSchema, response);
}
/**
 * Exclude an account-scoped (self) grouped alert (KIR-2363).
 */
export async function excludeAccountAlert(client, alertKey, body) {
    const validatedBody = parseSchema(excludeAlertRequestSchema, body);
    const response = await client.post(`/account/alerts/${encodeURIComponent(alertKey)}/exclude`, validatedBody);
    return parseSchema(excludedAlertStateSchema, response);
}
/**
 * Remove the exclusion from an account-scoped (self) grouped alert.
 */
export async function removeAccountAlertExclusion(client, alertKey) {
    const response = await client.delete(`/account/alerts/${encodeURIComponent(alertKey)}/exclude`);
    return parseSchema(excludedAlertStateSchema, response);
}
/**
 * Preview eligibility counts for a bulk alert selection (KIR-3252).
 *
 * Drives the bulk-action bar labels and confirmation-modal copy: how many of
 * the selected alerts will change vs. are already in the target state vs. are
 * ineligible. The selection is a descriptor (`keys` | `query`) — `query` lets
 * the backend resolve "all matching the current filter across pages" without
 * the client enumerating ids.
 */
export async function getOrganizationBulkAlertEligibility(client, organizationId, body) {
    const validatedBody = parseSchema(bulkAlertEligibilityRequestSchema, body);
    const response = await client.post(`/organizations/${organizationId}/alerts/bulk/eligibility`, validatedBody);
    return parseSchema(bulkAlertEligibilityResponseSchema, response);
}
/**
 * Apply a bulk alert action to an organization selection (KIR-3252).
 *
 * `action ∈ { mute, unmute, exclude, remove-exclusion }`. All-or-nothing: the
 * backend applies the action as a single set-based statement and returns
 * `{ action, changedCount }` — the number of alerts that actually transitioned.
 * Already-in-target-state and exclusion-ineligible alerts are silently skipped
 * (disclosed up front by {@link getOrganizationBulkAlertEligibility}); there is
 * no partial-success result and no undo.
 */
export async function applyOrganizationBulkAlertAction(client, organizationId, action, body) {
    const validatedAction = parseSchema(bulkAlertActionSchema, action);
    const validatedBody = parseSchema(bulkAlertApplyRequestSchema, body);
    const response = await client.post(`/organizations/${organizationId}/alerts/bulk/${validatedAction}`, validatedBody);
    return parseSchema(bulkAlertApplyResponseSchema, response);
}
/**
 * Preview eligibility counts for a bulk alert selection over the authenticated
 * user's own alerts (KIR-3252, personal scope). Personal-scope twin of
 * {@link getOrganizationBulkAlertEligibility} — same selection descriptor and
 * response shape, no org RBAC.
 */
export async function getAccountBulkAlertEligibility(client, body) {
    const validatedBody = parseSchema(bulkAlertEligibilityRequestSchema, body);
    const response = await client.post(`/account/alerts/bulk/eligibility`, validatedBody);
    return parseSchema(bulkAlertEligibilityResponseSchema, response);
}
/**
 * Apply a bulk alert action to the authenticated user's own selection
 * (KIR-3252, personal scope). Personal-scope twin of
 * {@link applyOrganizationBulkAlertAction}: all-or-nothing, returns
 * `{ action, changedCount }`, no partial result and no undo.
 */
export async function applyAccountBulkAlertAction(client, action, body) {
    const validatedAction = parseSchema(bulkAlertActionSchema, action);
    const validatedBody = parseSchema(bulkAlertApplyRequestSchema, body);
    const response = await client.post(`/account/alerts/bulk/${validatedAction}`, validatedBody);
    return parseSchema(bulkAlertApplyResponseSchema, response);
}
//# sourceMappingURL=alerts.js.map